import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { PageHeader, Btn } from "../components/common";
import { ShieldCheck, ShieldOff, Download, RefreshCw, Copy, AlertTriangle, Send, Mail } from "lucide-react";
import { RichText } from "../components/RichText";
import { emailSafeHtml } from "../lib/emailPreview";

export default function Perfil() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");
  const [signatureHtml, setSignatureHtml] = useState("");
  const [lcExt, setLcExt] = useState("");
  const [savingPerfil, setSavingPerfil] = useState(false);

  const load = () => api.get("/mfa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/me").then(({ data }) => {
      setSignatureHtml(data?.assinatura_email || "");
      setLcExt(data?.letscall_extension || "");
    }).catch(() => {});
  }, []);

  const savePerfil = async () => {
    setSavingPerfil(true);
    try {
      await Promise.all([
        api.put("/me/signature", { assinatura_email: signatureHtml }),
        api.put("/me/letscall-extension", { letscall_extension: lcExt }),
      ]);
      toast.success("Perfil guardado.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPerfil(false); }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/mfa/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyEnable = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/mfa/verify-enable", { code });
      setRecovery(data.recovery_codes || []);
      setSetup(null); setCode("");
      toast.success("MFA activada com sucesso.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    if (!confirmCode) return toast.error("Introduza um codigo para confirmar.");
    setLoading(true);
    try {
      await api.post("/mfa/disable", { code: confirmCode });
      toast.success("MFA desactivada.");
      setConfirmCode(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const regen = async () => {
    if (!confirmCode) return toast.error("Introduza um codigo TOTP actual para regenerar.");
    setLoading(true);
    try {
      const { data } = await api.post("/mfa/regen-recovery", { code: confirmCode });
      setRecovery(data.recovery_codes || []);
      setConfirmCode("");
      toast.success("Codigos de recuperacao regenerados.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const download = () => {
    const blob = new Blob([`SaaS-Hub - codigos de recuperacao MFA\n${user?.email || ""}\n\n${recovery.join("\n")}\n\nCada codigo pode ser usado uma so vez. Guarde-os num sitio seguro.`],
      { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "saas-hub-recovery-codes.txt"; a.click();
  };

  return (
    <div className="max-w-2xl">
      <PageHeader overline="A minha conta" title="O meu perfil" />

      <div className="border border-tint rounded-lg bg-white p-5 mb-8 space-y-5" data-testid="perfil-assinatura-section">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail size={16} className="text-blue" />
            <h3 className="text-sm font-bold text-navy">Assinatura de email</h3>
          </div>
          <p className="text-sm text-slate-600">
            Esta assinatura e acrescentada aos emails enviados a partir de uma ficha de conta/contacto (quando a opcao "Incluir a minha assinatura" esta activa) e nas respostas de tickets. Aparece automaticamente na pre-visualizacao ao compor um email.
          </p>
        </div>
        <RichText value={signatureHtml} onChange={setSignatureHtml} minHeight={120} testid="perfil-signature-editor" />
        {signatureHtml.trim() && (
          <div>
            <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Pre-visualizacao</label>
            <div
              data-testid="perfil-signature-preview"
              className="border border-tint rounded-lg p-3 bg-slate-50 text-sm"
              style={{ fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif", color: "#0B2545", lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: emailSafeHtml(signatureHtml) }}
            />
          </div>
        )}
        <div>
          <label className="block text-xs uppercase text-muted2 font-semibold mb-1">Extensao LetsCall One</label>
          <input
            value={lcExt}
            onChange={(e) => setLcExt(e.target.value)}
            placeholder="Ex: 201"
            data-testid="perfil-letscall-extension"
            className="w-full max-w-xs px-3 py-2 border border-tint rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue/20"
          />
          <p className="text-[11px] text-muted2 mt-1">
            Extensao do teu telefone no LetsCall One. Sera usada automaticamente ao clicares em "Ligar" no CRM.
          </p>
        </div>
        <Btn onClick={savePerfil} disabled={savingPerfil} testid="perfil-save">{savingPerfil ? "A guardar..." : "Guardar perfil"}</Btn>
      </div>

      <PageHeader overline="Seguranca" title="MFA (autenticacao em dois passos)" />
      <p className="text-sm text-slate-600 mb-6 max-w-lg">
        Ative a autenticacao em dois passos (TOTP) usando Authy, Google Authenticator, 1Password, Microsoft Authenticator ou Bitwarden. Como failsafe, pode usar codigos de recuperacao ou pedir um codigo por Telegram.
      </p>

      {status && !status.mfa_enabled && !setup && !recovery && (
        <div className="border border-tint rounded-lg bg-white p-5" data-testid="mfa-not-enabled">
          <div className="flex items-center gap-2 mb-3">
            <ShieldOff size={16} className="text-muted2" />
            <div className="font-semibold text-navy">MFA desactivada</div>
            {status.mfa_forced && (
              <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded ml-2">
                <AlertTriangle size={9} className="inline" /> Obrigatoria pelo super admin
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 mb-4">Configure agora para reforcar a seguranca da sua conta.</p>
          <Btn onClick={startSetup} disabled={loading} testid="mfa-start-setup">
            <ShieldCheck size={14} /> Iniciar setup MFA
          </Btn>
        </div>
      )}

      {setup && (
        <div className="border border-tint rounded-lg bg-white p-5" data-testid="mfa-setup">
          <div className="font-semibold text-navy mb-2">1. Adicione a app autenticadora</div>
          <p className="text-sm text-slate-600 mb-3">Aponte a camara da sua app (Authy, Google Authenticator, 1Password, etc) para o QR abaixo.</p>
          <div className="flex flex-col sm:flex-row items-start gap-4 mb-4">
            <img src={setup.qr_data_uri} alt="QR MFA" className="w-48 h-48 border border-tint rounded" data-testid="mfa-qr" />
            <div className="flex-1">
              <label className="text-xs text-slate-500 uppercase font-semibold">Chave manual</label>
              <div className="flex items-center gap-1.5 mt-1">
                <code className="text-[11px] bg-slate-50 border border-tint rounded px-2 py-1.5 font-mono flex-1 break-all">{setup.secret}</code>
                <button onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copiado"); }}
                  data-testid="mfa-copy-secret" className="p-1.5 text-navy hover:bg-tint rounded">
                  <Copy size={12} />
                </button>
              </div>
            </div>
          </div>
          <div className="font-semibold text-navy mb-2">2. Introduza o codigo de 6 digitos</div>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.trim())}
              data-testid="mfa-verify-code" autoComplete="one-time-code" inputMode="numeric" maxLength={6}
              placeholder="123456" className="flex-1 px-3 py-2 border border-tint rounded-lg text-sm" />
            <Btn onClick={verifyEnable} disabled={loading || code.length !== 6} testid="mfa-verify-submit">Verificar e activar</Btn>
          </div>
        </div>
      )}

      {recovery && (
        <div className="border border-amber-200 rounded-lg bg-amber-50 p-5" data-testid="mfa-recovery-codes">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-amber-700" />
            <div className="font-semibold text-amber-900">Guarde estes codigos de recuperacao</div>
          </div>
          <p className="text-xs text-amber-800 mb-3">Cada codigo pode ser usado <b>uma so vez</b> caso perca acesso a app autenticadora. Nao serao mostrados novamente. Descarregue-os agora.</p>
          <div className="grid grid-cols-2 gap-1 bg-white border border-amber-200 rounded p-3 font-mono text-[13px] mb-3">
            {recovery.map((c, i) => <div key={i} className="text-navy">{c}</div>)}
          </div>
          <div className="flex gap-2">
            <Btn onClick={download} testid="mfa-download-recovery"><Download size={13} /> Descarregar</Btn>
            <Btn variant="ghost" onClick={() => setRecovery(null)}>Fechar</Btn>
          </div>
        </div>
      )}

      {status && status.mfa_enabled && !recovery && (
        <div className="border border-emerald-200 rounded-lg bg-white p-5 space-y-4" data-testid="mfa-enabled">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" />
            <div className="font-semibold text-navy">MFA activa</div>
            <span className="text-[10px] text-slate-500">desde {(status.mfa_enabled_at || "").slice(0, 10)}</span>
            {status.mfa_forced && (
              <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded">Obrigatoria</span>
            )}
          </div>
          <div className="text-sm text-slate-600">
            Codigos de recuperacao: <b>{status.recovery_codes_total - status.recovery_codes_used}/{status.recovery_codes_total}</b> disponiveis.
            {status.telegram_configured
              ? <span className="text-emerald-700 ml-2">Telegram OTP disponivel</span>
              : <span className="text-amber-700 ml-2">Configure Telegram para receber OTP failsafe</span>}
          </div>
          {status.telegram_configured && (
            <div className="border-t border-tint pt-3 flex items-center justify-between gap-3">
              <div className="text-[12px] text-muted2 flex-1">
                Receber uma mensagem de teste no seu Telegram para confirmar que esta a receber correctamente.
              </div>
              <Btn variant="ghost" onClick={async () => {
                try {
                  const { data } = await api.post("/auth/mfa/telegram-test");
                  toast.success(data.message || "Mensagem enviada - verifique o Telegram.");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha a enviar.");
                }
              }} testid="mfa-tg-test">
                <Send size={12} /> Testar OTP Telegram
              </Btn>
            </div>
          )}
          <div className="border-t border-tint pt-3">
            <label className="text-xs uppercase text-muted2 font-semibold">Confirmar com codigo TOTP actual</label>
            <div className="flex gap-2 mt-1">
              <input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.trim())}
                data-testid="mfa-confirm-code" placeholder="Codigo actual" inputMode="text"
                className="flex-1 px-3 py-2 border border-tint rounded-lg text-sm" />
              <Btn variant="ghost" onClick={regen} disabled={loading || !confirmCode} testid="mfa-regen">
                <RefreshCw size={12} /> Regenerar codigos
              </Btn>
              {!status.mfa_forced && (
                <Btn variant="ghost" onClick={disable} disabled={loading || !confirmCode} testid="mfa-disable">
                  <ShieldOff size={12} /> Desactivar
                </Btn>
              )}
            </div>
            {status.mfa_forced && (
              <p className="text-[11px] text-amber-800 mt-1">MFA e obrigatoria por politica; contacte o super admin para desactivar.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
